import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'aura-selection-card',
  imports: [CommonModule, MatIconModule],
  templateUrl: './selection-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectionCardComponent),
      multi: true,
    },
  ],
  host: {
    '[class.block]': 'true',
    '(click)': 'select()',
    '[attr.aria-checked]': 'selected()',
    '[attr.role]': '"radio"',
    '[attr.tabindex]': 'disabled() ? -1 : 0',
    '(keydown.space)': 'select()',
    '(keydown.enter)': 'select()',
  },
})
export class SelectionCardComponent implements ControlValueAccessor {
  /**
   * The label displayed on the card.
   */
  label = input.required<string>();

  /**
   * Optional description text.
   */
  description = input<string>();

  /**
   * Optional icon name (Material Icon).
   */
  icon = input<string>();

  /**
   * The value this card represents when selected.
   */
  value = input.required<unknown>();

  /**
   * Internal state for selection.
   */
  selected = signal<boolean>(false);

  /**
   * Internal state for disabled status.
   */
  disabled = signal<boolean>(false);

  // ControlValueAccessor implementation
  private onChange: (value: unknown) => void = () => {
    // Default implementation
  };
  private onTouched: () => void = () => {
    // Default implementation
  };

  private group: { select(value: unknown): void } | null = null;

  /**
   * Internal method used by SelectionCardGroup to register itself.
   */
  registerGroup(group: { select(value: unknown): void }) {
    this.group = group;
  }

  writeValue(value: unknown): void {
    this.selected.set(value === this.value());
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /**
   * Selects this card and notifies the group or form control.
   */
  select(): void {
    if (this.disabled()) return;

    if (this.group) {
      this.group.select(this.value());
    } else {
      this.selected.set(true);
      this.onChange(this.value());
      this.onTouched();
    }
  }
}
